import "./PanelLayout.scss";

import Button from "../../models/Button";
import { useRedraw } from "../../models/useRerender";
import OnePanelPage from "./OnePanelPage";
import { useOnChangeWatcher } from "../../models/useOnChangeWatcher";
import { useEffect, useRef, useState } from "react";
import Device from "../../models/Device";
import PanelUtils from "./PanelUtils";
import { PanelConfig } from "./PanelConfig";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ExtProperties from "./ExtPropeties";
import { faGear } from "@fortawesome/free-solid-svg-icons";
import { itx } from "../../translationItx/itx";

function Header(props: { panel: Device; panelConfig: PanelConfig }) {
  const { panel, panelConfig } = props;
  const [maxPageNumber, setMaxPageNumber] = useState(1);
  const rootRef = useRef<HTMLDivElement>(null);

  const orientation = PanelUtils.getOrientation(panel, panelConfig);

  const minWidthHorizontal = useRef(400);
  const minWidthVertical = useRef(600);

  const [extPropertiesOpen, setExtPropertiesOpen] = useState(false);

  useEffect(() => {
    if (orientation === "H") {
      minWidthHorizontal.current = Math.max(
        minWidthHorizontal.current,
        rootRef.current?.getBoundingClientRect().width ?? 0,
      );
    } else {
      minWidthVertical.current = Math.max(
        minWidthVertical.current,
        rootRef.current?.getBoundingClientRect().width ?? 0,
      );
    }
  });

  useEffect(() => {
    const n = PanelUtils.getNumberOfPages(panel, panelConfig);
    setMaxPageNumber(n);
  }, [panel, panelConfig]);

  if (!panelConfig?.pageNumberId) {
    return null;
  }

  const supportsOrientation = panelConfig?.orientation?.id !== undefined;

  return (
    <div
      ref={rootRef}
      className="estadaRow w100 left"
      style={{
        minWidth: orientation === "H" ? minWidthHorizontal.current + "px" : minWidthVertical.current + "px",
        marginBottom: "10px",
        padding: "10px",
        paddingLeft: "20px",
        marginLeft: "-10px",
        marginRight: "10px",
        backgroundColor: "var(--backgroundColorGrey)",
        color: "var(--white1)",
      }}
    >
      <div style={{ marginRight: "10px" }}> {itx("PanelLayoutMain.anzahlSeiten", "Anzahl Seiten:")}</div>
      <input
        style={{ width: "50px", height: "30px" }}
        type="number"
        value={maxPageNumber}
        step={1}
        min={1}
        max={100}
        onChange={(e) => {
          const n = parseInt(e.target.value);
          setMaxPageNumber(n);
          PanelUtils.setNumberOfPages(panel, panelConfig, n);
        }}
      />

      <div style={{ flexGrow: 1 }} />

      {supportsOrientation && (
        <div style={{ marginLeft: "20px", marginRight: "10px" }}>
          {" "}
          {itx("PanelLayoutMain.ausrichtung", "Ausrichtung:")}
        </div>
      )}
      {supportsOrientation && (
        <select
          style={{ height: "30px" }}
          value={orientation}
          onChange={(e) => {
            const orientation = e.target.value as "H" | "V";
            PanelUtils.setOrientation(panel, panelConfig, orientation);
          }}
        >
          <option value="H">{itx("PanelLayoutMain.horizontal", "Horizontal")}</option>
          <option value="V">{itx("PanelLayoutMain.vertikal", "Vertikal")}</option>
        </select>
      )}

      {panelConfig.extProperties && (
        <div
          className="button circle"
          style={{
            position: "relative",
            width: "30px",
            height: "30px",
            cursor: "pointer",
            backgroundColor: extPropertiesOpen ? "var(--primary-color)" : undefined,
          }}
          onClick={() => setExtPropertiesOpen(true)}
        >
          <FontAwesomeIcon icon={faGear} />
          {extPropertiesOpen && (
            <div style={{ position: "relative", top: "0", right: "0", width: "0px" }}>
              <ExtProperties
                title={itx("PanelLayoutMain.eigenschaftenDesPanels", "Eigenschaften des Panels")}
                panel={panel as Button}
                panelMappingConfig={panelConfig}
                pageIndex={-2}
                extProperties={panelConfig.extProperties}
                onClose={() => setExtPropertiesOpen(false)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PanelLayoutUI(props: { panel: Button }) {
  const { panel } = props;
  const redraw = useRedraw();

  useOnChangeWatcher(panel.knxInstanceData);
  useOnChangeWatcher(panel);

  const config = panel.panelConfig;
  const panelConfig = config?.panelConfig;
  const homePageConfig = config?.homePageConfig;

  useEffect(() => {
    PanelUtils.removeCellsWithoutFunction(panel);
  }, [panel, panel.buttonChannels]);

  const maxPageNumber = PanelUtils.getNumberOfPages(panel, panelConfig);

  if (!panelConfig) {
    return <div>{itx("PanelLayoutMain.noPanelLayout", "No panel layout config found")}</div>;
  }

  if (!panel.panelCells) {
    panel.panelCells = { cells: [] };
  }
  if (panelConfig) {
    return (
      <div
        className="panelLayoutUI estadaColumn left top h100 w100"
        style={{
          paddingRight: "10px",
          paddingLeft: "10px",
          alignItems: "stretch",
        }}
      >
        <Header panel={panel} panelConfig={panelConfig} />

        {homePageConfig && (
          <OnePanelPage
            panel={panel as Button}
            redraw={redraw}
            key={"H"}
            pageIndex={-1}
            panelCells={panel.panelCells!}
            panelConfig={homePageConfig}
          />
        )}

        {Array.from({ length: maxPageNumber }, (_, index) => (
          <OnePanelPage
            panel={panel as Button}
            redraw={redraw}
            key={index}
            pageIndex={index}
            panelCells={panel.panelCells!}
            panelConfig={panelConfig}
          />
        ))}
      </div>
    );
  } else {
    return <div>{itx("PanelLayoutMain.noPanelLayout", "No panel layout found")}</div>;
  }
}
